import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Star, Download, Trash2, Loader2, Image as ImageIcon, RefreshCw } from 'lucide-react';
import { toast } from '../utils/toast';
const API = window.__API_BASE + '/api';

/**
 * AI Photoshop Gallery — user-scoped, global.
 *
 * Renders the user's "okayed" iterations across all AI Photoshop runs.
 * Lives as a tabbed sibling to "Recent Runs" on the AI Photoshop recipe
 * detail page — in the MAIN CONTENT PANEL (~800-1000px wide) directly
 * below the Source Image input form.
 *
 * Layout decisions:
 *   - 2-up on narrow screens, 3-up md, 4-up lg — adapts to viewport
 *   - aspect-square (1:1) — both portrait & landscape edits read cleanly
 *   - max-height with internal scroll so the gallery NEVER pushes the
 *     Source Image / Generate form off-screen (this was the "top panel
 *     too small" bug — 3 portrait tiles at 600px each was eating all
 *     the page real-estate)
 *   - hover overlay: icon-only round buttons (Download / ★ / 🗑)
 *   - engine pill: short 'GPT' / 'NANO' code, top-left of thumb
 */
export default function AIPhotoshopGalleryPanel({ onReopen, refreshKey = 0 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ai-photoshop/gallery`);
      setItems(res.data?.items || []);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load gallery';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const handleDelete = async (entryId) => {
    if (!window.confirm('Remove this image from your Gallery?')) return;
    setDeletingId(entryId);
    try {
      await axios.delete(`${API}/ai-photoshop/gallery/${entryId}`);
      setItems(prev => prev.filter(i => i.id !== entryId));
      toast.success('Removed from Gallery');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10" data-testid="gallery-loading">
        <Loader2 className="w-4 h-4 text-yellow-400 animate-spin" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-3 text-center" data-testid="gallery-empty">
        <div className="w-9 h-9 rounded-full bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center mb-2">
          <Star className="w-3.5 h-3.5 text-yellow-400/70" />
        </div>
        <p className="text-xs font-medium text-white/80 mb-1">No edits yet</p>
        <p className="text-[10px] text-white/40 leading-relaxed max-w-[180px]">
          Inside a workbench, click <span className="text-yellow-400 font-semibold">★ Add to Gallery</span> on
          an iteration to save it here.
        </p>
      </div>
    );
  }

  // Engine badge: single short token, no wrapping. We previously rendered
  // "GPT-IMG-2" which wrapped onto 3 lines inside the 240-px wide tiles.
  // Short codes only.
  const engineShort = (e) => {
    const k = (e || '').toLowerCase();
    if (k.includes('gpt')) return 'GPT';
    if (k.includes('nano') || k.includes('gemini')) return 'NANO';
    return '';
  };

  return (
    <div className="w-full" data-testid="gallery-panel">
      <div className="flex items-center justify-between mb-2 px-0.5">
        <p className="text-[10px] font-mono text-white/40 uppercase tracking-wider">
          {items.length} item{items.length !== 1 ? 's' : ''}
        </p>
        <button
          onClick={load}
          className="p-1 rounded hover:bg-white/5 transition-colors"
          title="Refresh"
          data-testid="gallery-refresh"
        >
          <RefreshCw className="w-3 h-3 text-white/40" />
        </button>
      </div>
      {/* Cap the gallery height so it scrolls internally instead of
          pushing the Source Image form / Generate button off-screen.
          ~360px gives ~2 rows visible on most viewports — enough to
          glance the recent okayed edits without dominating the page. */}
      <div
        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 overflow-y-auto pr-1"
        style={{ maxHeight: 'min(360px, 50vh)' }}
        data-testid="gallery-grid"
      >
        {items.map((it) => {
          const e = engineShort(it.engine);
          return (
            <div
              key={it.id}
              className="group relative rounded-lg overflow-hidden border border-white/10 hover:border-yellow-500/40 transition-all bg-black/40"
              data-testid={`gallery-item-${it.id}`}
            >
              <div className="aspect-square w-full bg-black/60 flex items-center justify-center relative">
                {it.image_url ? (
                  <img
                    src={it.image_url}
                    alt={it.edit_instruction || 'Gallery edit'}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    onError={(ev) => { ev.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <ImageIcon className="w-5 h-5 text-white/20" />
                )}
                {/* Tiny engine pill — top-left, never wraps */}
                {e && (
                  <span
                    className="absolute top-1 left-1 text-[8px] font-bold px-1 py-0.5 rounded bg-black/70 text-white/70 uppercase tracking-wider whitespace-nowrap"
                    title={`Generated with ${it.engine}`}
                  >
                    {e}
                  </span>
                )}
                {/* Hover overlay — icon-only buttons overlay the thumb,
                    not below it, so caption stays readable. */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5"
                  style={{ background: 'rgba(0,0,0,0.55)' }}
                >
                  <a
                    href={it.image_url}
                    download={`gallery_${it.id.slice(0, 8)}.png`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(ev) => ev.stopPropagation()}
                    className="w-7 h-7 rounded-full bg-white/10 backdrop-blur hover:bg-cyan-500 transition-colors flex items-center justify-center"
                    data-testid={`gallery-download-${it.id}`}
                    title="Download PNG"
                  >
                    <Download className="w-3 h-3 text-white" />
                  </a>
                  {onReopen && (
                    <button
                      onClick={(ev) => { ev.stopPropagation(); onReopen(it); }}
                      className="w-7 h-7 rounded-full bg-yellow-500/30 backdrop-blur hover:bg-yellow-500 transition-colors flex items-center justify-center"
                      data-testid={`gallery-reopen-${it.id}`}
                      title="Re-open in workbench"
                    >
                      <Star className="w-3 h-3 text-white" />
                    </button>
                  )}
                  <button
                    onClick={(ev) => { ev.stopPropagation(); handleDelete(it.id); }}
                    disabled={deletingId === it.id}
                    className="w-7 h-7 rounded-full bg-white/10 backdrop-blur hover:bg-red-500 transition-colors flex items-center justify-center disabled:opacity-30"
                    data-testid={`gallery-delete-${it.id}`}
                    title="Remove from Gallery"
                  >
                    {deletingId === it.id ? (
                      <Loader2 className="w-3 h-3 text-white animate-spin" />
                    ) : (
                      <Trash2 className="w-3 h-3 text-white" />
                    )}
                  </button>
                </div>
              </div>
              {/* Caption — single-line truncate with hover-tooltip */}
              {it.edit_instruction && (
                <div className="px-1.5 py-1 border-t border-white/5">
                  <p
                    className="text-[10px] text-white/55 truncate leading-tight"
                    title={it.edit_instruction}
                  >
                    {it.edit_instruction}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
